/**
 * MyReviews Page
 *
 * Protected page showing all reviews by the authenticated user.
 * Features glassmorphic card design with staggered animations,
 * glow effects, and orb backgrounds.
 *
 * SSR: Uses useIsMounted() for client-only rendering. Reviews
 * are fetched client-side after mount (protected page, no SEO need).
 * Adds <meta name="robots" content="noindex,nofollow"> via PageHead.
 */

import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import useIsMounted from '../hooks/useIsMounted.mjs';
import { buildArticleUrl } from '../utils/buildArticleUrl.mjs';
import {
    fetchAllMyRatings,
    deleteMyRating,
    openRatingModal,
    selectMyReviews,
    selectMyReviewsLoading,
    selectMyReviewsError,
} from '../slices/ratingsSlice.mjs';

// ── Page title + noindex meta ───────────────────────────────────────────────────
const PAGE_TITLE = 'My Reviews | Nexus of Truth';

// ── Helpers ─────────────────────────────────────────────────────────────────────

/** Format a date as "2 days ago", "3 weeks ago", etc. */
function timeAgo(dateStr) {
    if (!dateStr) return '';
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
}

/** Capitalize first letter of a quickChip value. */
function formatChip(chip) {
    if (!chip) return null;
    return chip.charAt(0).toUpperCase() + chip.slice(1).replace(/-/g, ' ');
}

// ── Sub-Components ──────────────────────────────────────────────────────────────

/** Filled/empty star SVG for inline display. */
function StarIcon({ filled }) {
    return (
        <svg
            className={`mr-card__star${filled ? '' : ' mr-card__star--empty'}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
    );
}

/** Individual review card with glassmorphic styling. */
function ReviewCard({ review, index, onEdit, onDelete }) {
    const [confirming, setConfirming] = useState(false);

    const handleDeleteClick = () => setConfirming(true);
    const handleCancel = () => setConfirming(false);
    const handleConfirm = () => {
        setConfirming(false);
        onDelete(review._id);
    };

    const stars = Array.from({ length: 5 }, (_, i) => i < review.rating);
    const chipLabel = formatChip(review.quickChip);
    // Build the canonical article URL using the enriched articleId object
    const articleUrl = (review.articleId && typeof review.articleId === 'object')
        ? buildArticleUrl(review.articleId)
        : '/';

    return (
        <div className="mr-card" style={{ '--mr-index': index }}>
            {/* Delete confirmation overlay */}
            {confirming && (
                <div className="mr-card__confirm">
                    <span className="mr-card__confirm-text">Delete this review?</span>
                    <button className="mr-card__confirm-btn mr-card__confirm-btn--yes" onClick={handleConfirm}>
                        Delete
                    </button>
                    <button className="mr-card__confirm-btn mr-card__confirm-btn--no" onClick={handleCancel}>
                        Cancel
                    </button>
                </div>
            )}

            {/* Header: stars + chip + date */}
            <div className="mr-card__header">
                <div className="mr-card__stars" aria-label={`${review.rating} out of 5 stars`}>
                    {stars.map((filled, i) => (
                        <StarIcon key={i} filled={filled} />
                    ))}
                </div>
                {chipLabel && <span className="mr-card__chip">{chipLabel}</span>}
                <span className="mr-card__date">{timeAgo(review.createdAt || review.updatedAt)}</span>
            </div>

            {/* Review text */}
            {review.reviewText && (
                <p className="mr-card__body">&ldquo;{review.reviewText}&rdquo;</p>
            )}

            {/* Footer: article link + actions */}
            <div className="mr-card__footer">
                <Link
                    to={articleUrl}
                    state={{ type: 'article' }}
                    className="mr-card__article-link"
                >
                    <svg className="mr-card__article-link-arrow" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                    {review.articleId?.title || 'View Article'}
                </Link>
                <div className="mr-card__actions">
                    <button className="mr-card__action" onClick={() => onEdit(review)}>
                        <svg className="mr-card__action-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
                        </svg>
                        Edit
                    </button>
                    <button className="mr-card__action mr-card__action--delete" onClick={handleDeleteClick}>
                        <svg className="mr-card__action-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.519.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 01.78.72l.5 6.5a.75.75 0 01-1.49.115l-.5-6.5a.75.75 0 01.71-.78zm2.84 0a.75.75 0 01.71.78l-.5 6.5a.75.75 0 11-1.49-.116l.5-6.5a.75.75 0 01.78-.72z" clipRule="evenodd" />
                        </svg>
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Loading skeleton cards. */
function ReviewSkeleton({ count = 3 }) {
    return Array.from({ length: count }, (_, i) => (
        <div key={i} className="mr-skeleton">
            <div className="mr-skeleton__line mr-skeleton__line--short" />
            <div className="mr-skeleton__line mr-skeleton__line--long" />
            <div className="mr-skeleton__line mr-skeleton__line--medium" />
        </div>
    ));
}

/** Empty state with orb BG and CTA. */
function EmptyState() {
    return (
        <div className="mr-empty">
            <div className="mr-empty__bg">
                <div className="mr-empty__orb mr-empty__orb--1" />
                <div className="mr-empty__orb mr-empty__orb--2" />
            </div>
            <div className="mr-empty__content">
                <svg className="mr-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                </svg>
                <h3 className="mr-empty__title">No Reviews Yet</h3>
                <p className="mr-empty__desc">
                    You haven't reviewed any articles yet. Start reading and share your thoughts!
                </p>
                <Link to="/" className="mr-empty__cta">
                    Explore Articles
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                    </svg>
                </Link>
            </div>
        </div>
    );
}

// ── Main Page Component ─────────────────────────────────────────────────────────

export default function MyReviews() {
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const reviews = useSelector(selectMyReviews);
    const loading = useSelector(selectMyReviewsLoading);
    const error = useSelector(selectMyReviewsError);
    const user = useSelector((state) => state.auth?.user);

    // Fetch reviews on mount (client-only)
    useEffect(() => {
        if (isMounted && user) {
            dispatch(fetchAllMyRatings());
        }
    }, [isMounted, user, dispatch]);

    // Set document title + inject noindex meta (client-only)
    useEffect(() => {
        if (!isMounted) return;
        document.title = PAGE_TITLE;

        // Inject noindex meta tag
        let meta = document.querySelector('meta[name="robots"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', 'robots');
            document.head.appendChild(meta);
        }
        meta.setAttribute('content', 'noindex,nofollow');

        return () => {
            // Cleanup: remove the noindex meta when navigating away
            const tag = document.querySelector('meta[name="robots"][content="noindex,nofollow"]');
            if (tag) tag.remove();
        };
    }, [isMounted]);

    // Redirect if not authenticated (client-side)
    useEffect(() => {
        if (isMounted && !user) {
            window.location.href = '/login';
        }
    }, [isMounted, user]);

    const handleEdit = useCallback((review) => {
        const articleSlug = review.articleId?.slug || review.articleId;
        dispatch(openRatingModal({
            articleId: articleSlug,
            initialStars: review.rating,
        }));
    }, [dispatch]);

    const handleDelete = useCallback((ratingId) => {
        dispatch(deleteMyRating({ ratingId }));
    }, [dispatch]);

    // SSR: render nothing until mounted
    if (!isMounted) {
        return <div className="mr" />;
    }

    // Not authenticated
    if (!user) {
        return null;
    }

    return (
        <div className="mr">
            {/* Header with orb BG */}
            <section className="mr-header">
                <div className="mr-header__bg">
                    <div className="mr-header__orb mr-header__orb--1" />
                    <div className="mr-header__orb mr-header__orb--2" />
                </div>
                <div className="mr-header__content">
                    <h1 className="mr-header__title">
                        My Reviews
                        {!loading && reviews.length > 0 && (
                            <span className="mr-header__count">{reviews.length}</span>
                        )}
                    </h1>
                    <p className="mr-header__sub">
                        All your article ratings and reviews in one place.
                    </p>
                </div>
            </section>

            {/* Reviews list */}
            <div className="mr-list">
                {/* Loading state */}
                {loading && <ReviewSkeleton count={4} />}

                {/* Error state */}
                {!loading && error && (
                    <div className="mr-error">
                        <p className="mr-error__message">{error}</p>
                    </div>
                )}

                {/* Empty state */}
                {!loading && !error && reviews.length === 0 && <EmptyState />}

                {/* Review cards */}
                {!loading && !error && reviews.length > 0 && reviews.map((review, i) => (
                    <ReviewCard
                        key={review._id}
                        review={review}
                        index={i}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                ))}
            </div>
        </div>
    );
}
